/* eslint-disable no-prototype-builtins */
import { isArray } from 'lodash';
import { useCallback, useState } from 'react';
import { handleTablePreferences, handleTablePreferencesEdit, handleTablePreferencesDelete, setTablePreferenceLoading } from 'src/redux/slices/tablePreferences';
import { dispatch, useSelector } from 'src/redux/store';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from './useGetCurrentUserId';
import useResponsive from './use-responsive';

const MUI_INTERNAL_FIELDS = ['__check__', '__reorder__', '__detail_panel_toggle__'];

const useColumnVisibilityChange = (tableTitle, defaultHidden, { defaultCombinedName = true } = {}) => {
    const userId = useGetCurrentUserId();
    const { tablePreferences: { results }, globalTablePreferences } = useSelector((state) => state.tablePreferences);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        const base = { __check__: true, __detail_panel_toggle__: false, ...(defaultHidden || {}) };
        if (defaultCombinedName) {
            return { ...base, lead_name: true, first_name: false, last_name: false };
        }
        return base;
    });
    const isMobile = useResponsive('down', 'sm');
    const [pinnedColumns, setPinnedColumns] = useState({
        right: isMobile ? ['actions'] : ['contact', 'viewDetails', 'actions']
    });
    const [isCombinedName, setIsCombinedName] = useState(defaultCombinedName);
    const [isEnforcedByPractice, setIsEnforcedByPractice] = useState(false);
    const handleColumnVisibilityChange = (newColumns, i) => {
        // Apply locally FIRST so the controlled DataGrid reflects the toggle
        // immediately. Without this, MUI fires onColumnVisibilityModelChange
        // but the parent never updates state — the prop stays stale and the
        // checkbox click visually does nothing. The API roundtrip below then
        // re-saves the stale state, locking the user into their starting view.
        setColumnVisibilityModel(newColumns);
        const unVisibleColumns = Object.entries(newColumns)?.filter(([key, value]) => !value && !MUI_INTERNAL_FIELDS.includes(key))?.map(([key]) => key);
        const allColumns = i?.api?.getAllColumns()?.map((ele) => ele?.field)?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        const visibleColumns = allColumns?.filter((ele) => !unVisibleColumns.includes(ele));
        dispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, columns: visibleColumns } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    };
    const handleColumnOrderChange = (newColumns) => {
        dispatch(setTablePreferenceLoading(true));
        const filteredColumns = newColumns?.filter((f) => !MUI_INTERNAL_FIELDS.includes(f));
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, columns: filteredColumns } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    };

    const resetColumns = useCallback(() => {
        const defaultPinned = {
            left: [],
            right: isMobile ? ['actions'] : ['contact', 'viewDetails', 'actions']
        };
        setPinnedColumns(defaultPinned);

        const base = { __check__: true, __detail_panel_toggle__: false, ...(defaultHidden || {}) };
        const resetVisibility = defaultCombinedName
            ? { ...base, lead_name: true, first_name: false, last_name: false }
            : base;
        setColumnVisibilityModel(resetVisibility);
        setIsCombinedName(defaultCombinedName);

        // DELETE the user's preference record so the cascade falls through to practice defaults
        const tableData = getCurrentTableData(results, tableTitle);
        if (isArray(tableData) && tableData.length > 0 && tableData[0].id) {
            dispatch(setTablePreferenceLoading(true));
            dispatch(handleTablePreferencesDelete({
                practiceUserId: userId,
                tableId: tableData[0].id,
                tableName: tableTitle,
            }));
        }
    }, [isMobile, userId, tableTitle, setPinnedColumns, defaultHidden, defaultCombinedName, results]);

    const handleToggleCombineName = useCallback((value) => {
        setIsCombinedName(value);
        setColumnVisibilityModel(prev => ({
            ...prev,
            lead_name: value,
            first_name: !value,
            last_name: !value,
        }));
        dispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(results, tableTitle);
        const currentGlobalTableData = getCurrentTableData(globalTablePreferences?.results || [], tableTitle);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : currentGlobalTableData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        let payload = {
            body: { table_name: tableTitle, preferences: { ...currentPreferences, isCombinedName: value } },
            practiceUserId: userId
        };
        if (isArray(tableData) && tableData.length > 0) {
            payload = { ...payload, tableId: tableData[0].id };
            dispatch(handleTablePreferencesEdit(payload));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [results, globalTablePreferences, tableTitle, userId]);

    return { userId, columnVisibilityModel, handleColumnVisibilityChange, setColumnVisibilityModel, handleColumnOrderChange, pinnedColumns, setPinnedColumns, resetColumns, isCombinedName, setIsCombinedName, handleToggleCombineName, isEnforcedByPractice, setIsEnforcedByPractice };
};

export default useColumnVisibilityChange;
